import React, { useState } from 'react';
import { FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaTimes, FaRocket } from 'react-icons/fa';

// --- Mock Data (RESTORED) ---
const mockAlertsData = [
    {
        id: 1,
        type: 'CRITICAL',
        customer: 'BuildRight LLC',
        details: '— $67.5K, 92 days overdue, trust 58.',
        aiRecommendation: 'Escalate legal proceedings', // Simplified for button text
        color: 'red',
        invoice: '#5001',
        trustScore: 58,
        daysOverdue: 92,
        amount: '$67,500',
        aiPrediction: 'BuildRight LLC (Trust: 58) has high risk of non-payment. Recommended action: Escalate to legal within 48 hours. 90% win probability with automated RTA.',
    },
    {
        id: 2,
        type: 'CRITICAL',
        customer: 'FastTrack Delivery',
        details: '— $15.6K, 105 days overdue, trust 42.',
        aiRecommendation: 'Initiate legal proceedings',
        color: 'red',
        invoice: '#5002',
        trustScore: 42,
        daysOverdue: 105,
        amount: '$15,600',
        aiPrediction: 'FastTrack Delivery (Trust: 42) has extreme risk of non-payment. Recommended action: Initiate immediate legal action. 85% win probability.',
    },
    {
        id: 3,
        type: 'WARNING',
        customer: 'Metro Services',
        details: '— Committed to payment plan 12 days ago, no payment received.',
        aiRecommendation: 'Call to confirm payment plan',
        color: 'orange',
        invoice: '#4510',
        trustScore: 68,
        daysOverdue: 12,
        amount: '$19,800',
        aiPrediction: 'Metro Services (Trust: 68) is at risk of defaulting on plan. Recommended action: Send immediate follow-up call. Update CRM notes.',
    },
    {
        id: 4,
        type: 'WARNING',
        customer: 'Digital Dynamics',
        details: '— Aging increased 30→55 days, trust declining.',
        aiRecommendation: 'Proactive call to prevent aging',
        color: 'orange',
        invoice: '#4777',
        trustScore: 71,
        daysOverdue: 55,
        amount: '$22,500',
        aiPrediction: 'Digital Dynamics (Trust: 71) shows negative behavior signals. Recommended action: Initiate proactive communication flow. Goal: Secure promise-to-pay within 7 days.',
    },
    {
        id: 5,
        type: 'SUCCESS',
        customer: 'Acme Corp',
        details: '— Paid $45.2K early, trust 92.',
        aiRecommendation: 'Send thank you/upgrade terms',
        color: 'green',
        invoice: '#4002',
        trustScore: 92,
        daysOverdue: 0,
        amount: '$45,200',
        aiPrediction: 'Acme Corp (Trust: 92) is a high-value, reliable customer. Recommended action: Send personalized thank-you email. Review for potential premium services.',
    },
    {
        id: 6,
        type: 'INFO',
        customer: 'TechStart Inc',
        details: '— 15 days old, trust 81.',
        aiRecommendation: 'Auto-schedule standard reminder',
        color: 'blue',
        invoice: '#4856',
        trustScore: 81,
        daysOverdue: 15,
        amount: '$28,900',
        aiPrediction: 'TechStart Inc (Trust: 81) is within normal aging range. Recommended action: Auto-schedule standard 15-day reminder email.',
    },
];

// --- Helper Functions for Color and Icon Mapping (Consistent with Dark Theme) ---
const getColorClasses = (color) => {
    switch (color) {
        // Subtle background (low opacity), distinct border color
        case 'red': return { icon: FaExclamationTriangle, text: 'text-red-400', border: 'border-red-600/50', bg: 'bg-red-900/10' };
        case 'orange': return { icon: FaExclamationTriangle, text: 'text-orange-400', border: 'border-orange-600/50', bg: 'bg-orange-900/10' };
        case 'green': return { icon: FaCheckCircle, text: 'text-green-400', border: 'border-green-600/50', bg: 'bg-green-900/10' };
        case 'blue': return { icon: FaInfoCircle, text: 'text-blue-400', border: 'border-blue-600/50', bg: 'bg-blue-900/10' };
        default: return { icon: FaInfoCircle, text: 'text-zinc-400', border: 'border-zinc-700', bg: 'bg-zinc-800' };
    }
};

const AlertDetailModal = ({ isOpen, onClose, alert }) => {
    if (!isOpen || !alert) return null;

    const { text, border, bg, icon: Icon } = getColorClasses(alert.color);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
            <div className={`w-[95%] max-w-4xl rounded-xl p-5 bg-zinc-800 border ${border} shadow-2xl overflow-y-auto max-h-[90vh]`}>
                
                {/* Modal Header */}
                <div className="flex items-start justify-between pb-3 mb-4 border-b border-zinc-700">
                    <h2 className="text-xl font-bold text-zinc-100">
                        {alert.customer}: {alert.type} Alert
                    </h2>
                    <button onClick={onClose} className="transition text-zinc-500 hover:text-white">
                        <FaTimes className="w-5 h-5" />
                    </button>
                </div>

                {/* 1. Trust Score & AR Intelligence Block (Mimics image_548620.png) */}
                <div className={`p-4 mb-4 rounded-lg border ${border} ${bg} flex items-center justify-between`}>
                    <div className="flex items-center">
                        <FaRocket className={`w-6 h-6 mr-3 ${text}`} />
                        <div>
                            <p className="text-sm font-semibold text-zinc-100">The Oqlous Solution: AR Intelligence Dashboard</p>
                            <p className="text-xs text-zinc-400">Trust-based collection strategies predict late payers before they're overdue.</p>
                        </div>
                    </div>
                    
                    <div className="flex-shrink-0 ml-4 text-center">
                        <p className="text-xs uppercase text-zinc-400">Customer Trust Score</p>
                        <p className={`text-2xl font-extrabold ${text}`}>{alert.trustScore}</p>
                    </div>
                </div>

                {/* 2. AI Prediction Section */}
                <div className={`p-4 mb-5 rounded-lg border-l-4 ${border} bg-zinc-700/50`}>
                    <p className="mb-2 text-sm font-semibold text-zinc-200">AI Prediction Detail:</p>
                    <p className="text-sm text-zinc-300">
                        <span className="font-bold">{alert.aiPrediction.split('.')[0]}.</span>
                        <span className="text-zinc-400">{alert.aiPrediction.split('.').slice(1).join('.')}</span>
                    </p>
                </div>

                {/* 3. Dashboard Preview Table (Mimics image_54835e.png) */}
                <div className="mb-6 overflow-hidden border rounded-lg border-zinc-700">
                    <h3 className="px-4 py-3 text-sm font-semibold border-b text-zinc-100 border-zinc-700 bg-zinc-700/50">
                        PREVIEW: Invoice Data Snapshot
                    </h3>
                    <div className="p-4">
                        <table className="w-full text-xs table-auto">
                            <thead>
                                <tr className="text-left uppercase text-zinc-400">
                                    <th className="py-2 pr-4">INVOICE #</th>
                                    <th className="py-2 pr-4">CUSTOMER</th>
                                    <th className="py-2 pr-4">AMOUNT</th>
                                    <th className="py-2 pr-4">DAYS OVERDUE</th>
                                    <th className="py-2 pr-4">TRUST SCORE</th>
                                    <th className="py-2 pr-4">ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Single Row Preview */}
                                <tr className="border-t border-zinc-700/50">
                                    <td className="py-3 pr-4 font-medium text-zinc-200">{alert.invoice}</td>
                                    <td className="py-3 pr-4 font-medium text-blue-300">{alert.customer}</td>
                                    <td className="py-3 pr-4 font-extrabold text-white">{alert.amount}</td>
                                    <td className={`py-3 pr-4 font-medium ${text}`}>{alert.daysOverdue} days</td>
                                    <td className="py-3 pr-4">
                                        {/* Trust score background remains visible */}
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${getColorClasses(alert.color).bg.replace('/10', '/30')} border border-zinc-700`}>{alert.trustScore}</span>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <button className="px-3 py-1 text-xs font-semibold text-white transition bg-indigo-700 rounded-lg hover:bg-indigo-600">
                                            {alert.aiRecommendation.split(' ')[0]} {/* Use only the first word for the button */}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 4. Action Button Area */}
                <div className="flex justify-end pt-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-semibold transition duration-200 rounded-lg text-zinc-300 hover:bg-zinc-700">
                        Dismiss Alert
                    </button>
                    <button className="px-4 py-2 ml-3 text-sm font-semibold text-white transition duration-200 bg-indigo-600 rounded-lg hover:bg-indigo-500">
                        Execute {alert.aiRecommendation.split(' ')[0]}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Main Smart Alerts Component ---

const SmartAlerts = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);

    const openModal = (alert) => {
        setSelectedAlert(alert);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedAlert(null);
    };

    return (
        // Theme: bg-zinc-900, reduced padding, enables scrolling
        <div className="p-4 mt-8 text-zinc-100 bg-zinc-900 max-h-[calc(100vh-120px)] overflow-y-auto">

            <h1 className="pb-2 mb-6 text-2xl font-extrabold border-b text-zinc-100 border-zinc-700">
                Smart Alerts & AI Recommendations
            </h1>

            {/* Alert Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {mockAlertsData.map((alert) => {
                    const { icon: Icon, text, border, bg } = getColorClasses(alert.color);

                    return (
                        <div
                            key={alert.id}
                            onClick={() => openModal(alert)}
                            // Subtle card styling
                            className={`p-4 rounded-xl cursor-pointer shadow-lg transition duration-200 hover:shadow-xl hover:bg-zinc-700 border ${border} ${bg}`}
                        >
                            <div className="flex items-center mb-3">
                                <Icon className={`w-5 h-5 mr-3 ${text}`} />
                                <span className={`text-sm font-bold uppercase ${text}`}>{alert.type}</span>
                            </div>
                            
                            <p className="mb-1 text-sm font-bold text-zinc-100">
                                {alert.customer}
                                <span className="font-normal text-zinc-300">{alert.details}</span>
                            </p>
                            
                            <p className="pt-2 mt-3 text-xs italic border-t text-zinc-400 border-zinc-700/50">
                                AI: {alert.aiRecommendation}
                            </p>
                        </div>
                    );
                })}
            </div>

            <AlertDetailModal 
                isOpen={isModalOpen}
                onClose={closeModal}
                alert={selectedAlert}
            />
        </div>
    );
};

export default SmartAlerts;